/**
 * Sync status bar - shows offline/online status, pending sync count,
 * and WebSocket real-time connection status.
 */

import { useOfflineSync } from '../hooks/useOfflineSync';
import { useWebSocketContext } from '../context/WebSocketContext';

export function SyncStatusBar() {
  const { status, triggerSync } = useOfflineSync();

  // Don't show anything if online and no pending items
  if (status.isOnline && status.pendingCount === 0 && !status.isSyncing) {
    return null;
  }

  return (
    <div
      className={`
        flex items-center justify-between px-4 py-2 text-sm
        ${
          status.isOnline
            ? 'bg-surface-raised border-b border-rule'
            : 'bg-amber-500/20 border-b border-amber-500/30'
        }
      `}
    >
      <div className="flex items-center gap-2">
        {!status.isOnline ? (
          <>
            <OfflineIcon className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-medium">Offline</span>
            {status.pendingCount > 0 && (
              <span className="text-amber-400/70">
                · {status.pendingCount} pending
              </span>
            )}
          </>
        ) : status.isSyncing ? (
          <>
            <SyncIcon className="w-4 h-4 text-accent animate-spin" />
            <span className="text-ink-muted">Syncing...</span>
          </>
        ) : status.pendingCount > 0 ? (
          <>
            <PendingIcon className="w-4 h-4 text-ink-subtle" />
            <span className="text-ink-muted">
              {status.pendingCount} pending
            </span>
          </>
        ) : null}
      </div>

      {status.isOnline && status.pendingCount > 0 && !status.isSyncing && (
        <button
          type="button"
          onClick={triggerSync}
          className="text-accent hover:text-accent-bright text-sm font-medium"
        >
          Sync now
        </button>
      )}

      {status.lastError && (
        <span className="text-red-400 text-xs truncate max-w-32">
          {status.lastError}
        </span>
      )}
    </div>
  );
}

/**
 * Compact sync indicator for header.
 * Shows both offline sync status and WebSocket real-time connection status.
 */
export function SyncIndicator() {
  const { status } = useOfflineSync();
  const { connectionState, connected } = useWebSocketContext();

  // Offline state takes priority
  if (!status.isOnline) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-amber-500" />
        <span className="text-xs text-amber-400">Offline</span>
      </div>
    );
  }

  // Syncing state
  if (status.isSyncing) {
    return (
      <div className="flex items-center gap-1.5">
        <SyncIcon className="w-3 h-3 text-accent animate-spin" />
        <span className="text-xs text-ink-subtle">Syncing</span>
      </div>
    );
  }

  // Pending sync items
  if (status.pendingCount > 0) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-accent" />
        <span className="text-xs text-ink-subtle">{status.pendingCount}</span>
      </div>
    );
  }

  // WebSocket connecting
  if (connectionState === 'connecting') {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-xs text-ink-subtle">Connecting</span>
      </div>
    );
  }

  // WebSocket error or disconnected (but network online)
  if (connectionState === 'error' || (connectionState === 'disconnected' && status.isOnline)) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-status-in-progress" />
        <span className="text-xs text-ink-subtle">Online</span>
        {connectionState === 'error' && (
          <RealtimeOffIcon className="w-3 h-3 text-ink-subtle ml-1" title="Real-time disconnected" />
        )}
      </div>
    );
  }

  // Fully connected with WebSocket
  if (connected) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-status-in-progress" />
        <span className="text-xs text-ink-subtle">Live</span>
      </div>
    );
  }

  // Default online state
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full bg-status-in-progress" />
      <span className="text-xs text-ink-subtle">Online</span>
    </div>
  );
}

// Icons
function OfflineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path
        d="M2 2l12 12M6.5 4.5A5 5 0 0113 8M4 6.5A5 5 0 003 8M5 9.5a3 3 0 015.5 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SyncIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path
        d="M2.5 8a5.5 5.5 0 019.5-3.75M13.5 8a5.5 5.5 0 01-9.5 3.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 1.5v3h-3M4 11.5v3h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PendingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 4.5V8l2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RealtimeOffIcon({ className, title }: { className?: string; title?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-label={title}>
      <title>{title}</title>
      <path
        d="M2 2l12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M4 10a6 6 0 010-4M12 6a6 6 0 010 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeOpacity="0.5"
      />
    </svg>
  );
}
